import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBackendService } from '../../../../actions/infrastructures';
import { RotateCw, Filter, Maximize, Minimize, Sun, Moon, Download, Clock, Search } from 'lucide-react';
import LogFilterModal from '../../../../components/Modals/Server/LogFilterModal';
import LogQueryModal from '../../../../components/Modals/Server/LogQueryModal'

// Constants
const DEFAULT_FILTER = {
  filterPattern: '',
  timeRange: {
    type: 'relative',
    duration: 10,
    unitOfTime: 'minutes'
  }
};

const POLL_INTERVAL = 5000;

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logGroupName, setLogGroupName] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(DEFAULT_FILTER);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [logHeight, setLogHeight] = useState('h-96');
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  
  // Create a ref to store the interval ID
  const autoRefreshIntervalRef = useRef(null);
  const logsContainerRef = useRef(null);

  const { projectId, serviceId } = useParams();
  const dispatch = useDispatch();
  
  const serviceInfo = useSelector((state) => state?.infrastructures?.currentService);
  
  const fetchLogs = useCallback(async (filterData = currentFilter, isAutoRefresh = false) => {
    if (!logGroupName || !serviceId) {
      console.warn('Missing required parameters for fetching logs');
      return;
    }

    if (!isAutoRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/${serviceId}/service_logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          logGroupName: `/ecs/${logGroupName}`,
          ...filterData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      
      if (isAutoRefresh) {
        setLogs(prevLogs => {
          const existingLogSet = new Set(prevLogs.map(log => typeof log === 'object' ? log.id : log));
          const newLogs = data.logs.filter(log => !existingLogSet.has(typeof log === 'object' ? log.id : log));
          return [...prevLogs, ...newLogs];
        });
      } else {
        setLogs(data.logs || []);
      }
      
      // Update last refresh time
      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('Error fetching logs:', error);
      setError('Failed to fetch logs. Please try again.');
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  }, [serviceId, logGroupName, currentFilter]);

  const handleFilterApply = useCallback((filterData) => {
    setCurrentFilter(filterData);
    fetchLogs(filterData);
    setIsFilterModalOpen(false);
  }, [fetchLogs]);

  // Start/stop auto-refresh
  const setupAutoRefresh = useCallback(() => {
    // Clear any existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
    
    // Set up new interval if auto-refresh is enabled
    if (autoRefresh && logGroupName && serviceId) {
      autoRefreshIntervalRef.current = setInterval(() => {
        fetchLogs(currentFilter, true);
      }, POLL_INTERVAL);
    }
  }, [autoRefresh, logGroupName, serviceId, fetchLogs, currentFilter]);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh(prev => !prev);
  }, []);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
    setLogHeight(prev => prev === 'h-96' ? 'h-screen' : 'h-96');
  }, []);

  const toggleTheme = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  const downloadLogs = useCallback(() => {
    const logsText = logs.map(log => typeof log === 'string' ? log : JSON.stringify(log)).join('\n');
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-logs-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs]);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        await dispatch(getBackendService(projectId, serviceId));
      } catch (error) {
        console.error('Error fetching service details:', error);
        setError('Failed to fetch service details');
      }
    };

    fetchServiceDetails();
  }, [dispatch, projectId, serviceId]);

  useEffect(() => {
    if (serviceInfo) {
      const newLogGroupName = serviceInfo?.logGroupName !== "" ? serviceInfo?.logGroupName : `${serviceInfo?.gitHubRepo}-${serviceInfo?.branchUsed}`;
      setLogGroupName(newLogGroupName);
    }
  }, [serviceInfo]);

  // Set up auto-refresh when dependencies change
  useEffect(() => {
    setupAutoRefresh();
    
    // Clean up on unmount
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [setupAutoRefresh]);

  useEffect(() => {
    if (logGroupName) {
      fetchLogs();
    }
  }, [logGroupName, fetchLogs]);

  // Handle escape key to exit full screen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isFullScreen) {
        toggleFullScreen();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullScreen, toggleFullScreen]);

  // Added padding for full screen mode
  const mainContainerClasses = isFullScreen 
    ? `fixed inset-0 z-50 bg-white dark:bg-gray-900 p-4 flex flex-col overflow-hidden`
    : `space-y-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800`;

  const headerClasses = `flex flex-col sm:flex-row sm:justify-between sm:items-center pb-4 border-b border-gray-100 dark:border-gray-800 gap-4`;

  return (
    <div className={mainContainerClasses}>
      <div className={headerClasses}>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Service Logs</h2>
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currentFilter.filterPattern ? 
                `Filtered by: "${currentFilter.filterPattern}"` : 
                `Last ${currentFilter.timeRange.duration} ${currentFilter.timeRange.unitOfTime}`}
            </p>
            {lastRefreshTime && (
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                Last refresh: {lastRefreshTime.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          {/* Download Logs Button */}
          <button
            onClick={downloadLogs}
            className="p-2 rounded-full transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700 "
            title="Download logs"
          >
            <Download className="h-5 w-5" />
          </button>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700 "
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {/* Fullscreen Toggle Button */}
          <button
            onClick={toggleFullScreen}
            className="p-2 rounded-full transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700 "
            title={isFullScreen ? "Exit full screen" : "Enter full screen"}
          >
            {isFullScreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>

          {/* Refresh Button */}
          <button
            onClick={() => fetchLogs(currentFilter, false)}
            className={`
              p-2 rounded-full transition-all duration-200 relative
              ${loading ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'}
            `}
            disabled={loading}
          >
            <RotateCw 
              className={`h-5 w-5 transform ${loading ? 'animate-spin' : 'hover:rotate-180 transition-transform duration-500 ease-in-out'}`}
            />
          </button>

          {/* Auto-refresh Button - Now Circular */}
          <button
            onClick={toggleAutoRefresh}
            className={`
              p-2 rounded-full transition-all duration-200 relative bg-white dark:bg-gray-800
              ${autoRefresh 
                ? '' 
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700'}
            `}
            title={autoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
          >
            <div className="relative">
              <RotateCw 
                className={`h-5 w-5 ${autoRefresh ? 'animate-spin text-indigo-500 dark:text-indigo-400' : ''}`}
                style={{ animationDuration: '4s' }}
              />
              <span className={`
                absolute -top-1 -right-1 w-3 h-3 rounded-full
                ${autoRefresh ? 'bg-indigo-500 dark:bg-indigo-400' : 'bg-gray-300 dark:bg-gray-600'}
              `}></span>
            </div>
          </button>

          {/* Filter Button - Now Circular */}
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="p-2 rounded-full transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700 "
            title="Filter logs"
          >
            <Filter className="h-5 w-5" />
          </button>

          {/* CloudWatch Insights Query Button */}
          <button
            onClick={() => setIsQueryModalOpen(true)}  // We'll add this state
            className="p-2 rounded-full transition-all duration-200 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-200 dark:border-gray-700 "
            title="Run CloudWatch Insights query"
          >
            <Search className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Logs display area */}
      <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex-grow flex flex-col">
        {error ? (
          <div className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 border rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            <button
              onClick={() => fetchLogs(currentFilter, false)}
              className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            >
              <RotateCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        ) : loading && logs.length === 0 ? (
          <div className={`flex items-center justify-center ${isFullScreen ? 'flex-grow' : logHeight}`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className={`flex flex-col items-center justify-center ${isFullScreen ? 'flex-grow' : logHeight} text-gray-500 dark:text-gray-400`}>
            <p className="text-sm">No logs found</p>
            <p className="text-xs mt-1">Try adjusting your filter settings or refreshing</p>
          </div>
        ) : (
          <div 
            ref={logsContainerRef}
            className={`
              ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-800'} 
              p-0 overflow-auto ${isFullScreen ? 'flex-grow' : logHeight} font-mono text-sm leading-relaxed shadow-inner
            `}
          >
            <div className="p-4">
              {logs.map((log, index) => (
                <div 
                  key={index} 
                  className={`
                    px-3 py-1 rounded mb-1 hover:bg-opacity-50
                    ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-200'} 
                    ${index % 2 === 0 ? (darkMode ? 'bg-gray-800 bg-opacity-30' : 'bg-gray-50') : ''}
                  `}
                >
                  <div className="whitespace-pre-wrap break-words">{typeof log === 'string' ? log : JSON.stringify(log)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <LogFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleFilterApply}
        currentFilter={currentFilter}
        darkMode={darkMode}
      />

      {/* Query Modal */}
      <LogQueryModal
        isOpen={isQueryModalOpen}
        onClose={() => setIsQueryModalOpen(false)}
        logGroupName={logGroupName ? `/ecs/${logGroupName}` : ''}
        serviceId={serviceId}
        darkMode={darkMode}
      />
    </div>
  );
};

export default Logs;